import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Settings, Volume2, VolumeX, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { DOCK, RACCOURCIS, entreesRail } from "./nav";
import { useDockAction } from "./useDockAction";
import { Notifications, Avatar } from "./TopBar";
import { sonMicroActif, reglerSonMicro } from "./sonMicro";
import { useOutilsDock, FenetreOutil } from "@/components/outils/DockOutils";

const Bulle = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="right" className="at-popover px-3 py-1.5 text-sm font-medium">{label}</TooltipContent>
  </Tooltip>
);

const LienRail = ({ code, label, icon: Icon, path, prefixe, badge }) => {
  const { pathname } = useLocation();
  const actif = path === "/" ? pathname === "/" : pathname.startsWith(prefixe || path);
  return (
    <Bulle label={label}>
      <NavLink to={path} aria-label={label} data-testid={`rail-${code}`} className={`at-rail-btn ${actif ? "actif" : ""}`}>
        <Icon size={22} strokeWidth={1.9} />
        {badge > 0 && <span className="at-badge" data-testid={`rail-badge-${code}`}>{badge}</span>}
      </NavLink>
    </Bulle>
  );
};

const Raccourcis = () => {
  const { user } = useAuth();
  const agir = useDockAction();
  const outils = useOutilsDock();
  const [outil, setOutil] = useState(null);
  const codes = RACCOURCIS.filter((c) => user.navigation.dock.includes(c));
  if (!codes.length && !outils.length) return null;
  return (
    <Popover>
      <Bulle label="Raccourcis">
        <PopoverTrigger asChild>
          <button type="button" aria-label="Raccourcis" data-testid="rail-raccourcis" className="at-rail-btn"><Sparkles size={22} strokeWidth={1.9} /></button>
        </PopoverTrigger>
      </Bulle>
      <PopoverContent side="right" align="start" className="at-popover w-60 p-2" data-testid="rail-raccourcis-menu">
        {codes.map((c) => {
          const { icon: Icon, label } = DOCK[c];
          return <button key={c} type="button" onClick={() => agir(c)} className="w-full min-h-[44px] px-3 flex items-center gap-3 rounded-xl text-sm font-semibold hover:bg-[var(--surface-2)]" data-testid={`rail-raccourci-${c}`}><Icon size={17} />{label}</button>;
        })}
        {outils.map((o) => <button key={o.id} type="button" onClick={() => setOutil(o)} className="w-full min-h-[44px] px-3 flex items-center gap-3 rounded-xl text-sm font-semibold hover:bg-[var(--surface-2)] text-left" data-testid={`rail-outil-${o.id}`}>{o.nom}</button>)}
      </PopoverContent>
      <FenetreOutil outil={outil} onClose={() => setOutil(null)} />
    </Popover>
  );
};

const BoutonSons = () => {
  const [actif, setActif] = useState(sonMicroActif());
  const basculer = () => { reglerSonMicro(!actif); setActif(!actif); };
  return (
    <Bulle label={actif ? "Couper les sons" : "Activer les sons"}>
      <button type="button" onClick={basculer} aria-label="Sons" data-testid="rail-sons" className="at-rail-btn">{actif ? <Volume2 size={20} /> : <VolumeX size={20} />}</button>
    </Bulle>
  );
};

export const Rail = () => {
  const { user } = useAuth();
  const agir = useDockAction();
  const { pathname } = useLocation();
  const [pastilles, setPastilles] = useState({});
  useEffect(() => { if (user.role === "agent") api.get("/poste/pastilles").then((r) => setPastilles(r.data)).catch(() => {}); }, [user.role, pathname]);
  return (
    <nav data-testid="rail" aria-label="Navigation" className="at-rail hidden md:flex">
      <Bulle label="Rechercher · Ctrl K">
        <button type="button" onClick={() => agir("rechercher")} aria-label="Rechercher" data-testid="rail-rechercher" className="at-rail-btn"><DOCK.rechercher.icon size={22} strokeWidth={1.9} /></button>
      </Bulle>
      <Raccourcis />
      <span className="at-rail-sep" />
      {entreesRail(user).map((x, i) => (x === "sep" ? <span key={`sep${i}`} className="at-rail-sep" /> : <LienRail key={x.code} {...x} badge={pastilles[x.code]} />))}
      <div className="mt-auto flex flex-col items-center gap-1.5 pt-3">
        <Notifications />
        <LienRail code="parametres" label="Paramètres" icon={Settings} path="/parametres/compte" prefixe="/parametres" />
        <BoutonSons />
        <Avatar />
      </div>
    </nav>
  );
};
